/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useRef} from 'react';
import {noneSelectedQuery} from '../helpers/MapViewUtils';

interface UseFeatureLayerEffectResult {
  createSelectionEffect: (layer, where: string) => void;
  setLayerHighlighter: (layerRef, view) => void;
  highlightItem: (layer, where: string) => void;
  removeHighlight: (layer) => void;
  initializePopup: (view: __esri.View) => void;
}

/**
 * @useFeatureLayerEffect
 * React Hook to attach effects on FeatureLayer
 *
 *  ```typescript
 *   import {useFeatureLayerEffect} from '@/routes/tracking/hooks';
 *
 *   const {createSelectionEffect} = useFeatureLayerEffect();
 *  ```
 */
export const useFeatureLayerEffect = (): UseFeatureLayerEffectResult => {
  const actionRef           = useRef(null);
  const layerHighlighterRef = useRef(null);

  const createSelectionEffect = useCallback(function(layer: __esri.FeatureLayer, where: string) {
    if (layer) {
      const filterForEffect = {where};
      layer.featureEffect = {
        filter                : filterForEffect,
        excludedEffect        : 'opacity(50%)',
        excludedLabelsVisible : true
      };
    }
  }, []);

  const highlightItem = useCallback(async(layerRef: any, where: string) => {
    if (layerHighlighterRef.current && where !== noneSelectedQuery) {
      const selectedAssetQuery = layerRef?.createQuery();
      selectedAssetQuery.where = where;
      const queryResult = await layerRef.queryFeatures(selectedAssetQuery);
      actionRef.current = layerHighlighterRef.current.highlight(queryResult?.features?.[0]?.attributes.OBJECTID);
    } else {
      actionRef.current?.remove();
    }
  }, [layerHighlighterRef, actionRef]);

  const removeHighlight = useCallback((layer) => {
    actionRef.current?.remove();
    layer && (layer.featureEffect = null);
  }, [actionRef]);

  const setLayerHighlighter = useCallback(async function(layerRef, view) {
    if (layerRef && view) {
      const resultLayer = await view.whenLayerView(layerRef);
      layerHighlighterRef.current = resultLayer;
    }
  }, [layerHighlighterRef]);

  const initializePopup = useCallback(({popup}: __esri.View) => {
    popup.dockEnabled = false;
    popup.dockOptions = {
      buttonEnabled : false,
      breakpoint    : false
    };
    popup.headingLevel = 5;
    popup.viewModel.includeDefaultActions = false;
  }, []);

  return {
    createSelectionEffect,
    setLayerHighlighter,
    highlightItem,
    removeHighlight,
    initializePopup
  };
};
